import type { ReactNode } from 'react';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type TableState as ReactTableState,
  type VisibilityState,
  createTable,
  getCoreRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table';
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { z } from 'zod';

import type {
  ColumnBuilder,
  SharedColumnDef,
} from '../components/AutoCollection/createColumnBuilder';
import {
  type AutoCollectionHandle,
  type AutoCollectionStateInterface,
  type IDColumnFilter,
  type IDColumnSort,
  type IDColumnVisibility,
  filterFns,
  sortingFns,
} from '../components/AutoCollection/utils';

export const TableColumnVisibilityStateSchema = z.record(z.string(), z.boolean());

export const TableColumnFiltersStateSchema = z.array(
  z.object({
    id: z.string(),
    value: z.any(),
  }),
);

export const TableColumnOrderStateSchema = z.array(z.string());

export const TableStateSchema = z.object({
  columnOrder: TableColumnOrderStateSchema,
  columnVisibility: TableColumnVisibilityStateSchema,
  columnFilters: TableColumnFiltersStateSchema,
});

export type TableStateType = z.infer<typeof TableStateSchema>;

type StateTuple<T> = readonly [T, (value: T) => void];

export interface TableStateOverrides<K extends string = string, D = any> {
  columnOrder?: StateTuple<K[]>;
  columnVisibility?: StateTuple<VisibilityState>;
  columnFilters?: StateTuple<ColumnFiltersState>;
  globalFilter?: StateTuple<string>;
  rowOrder?: StateTuple<D[]>;
}

export interface TableStateConfig<
  D = any,
  C extends SharedColumnDef<D, any> = SharedColumnDef<D, any>,
> {
  /** Column builder instance. */
  builder: ColumnBuilder<D>;

  /** Column definitions. */
  columns: C[];

  /** Default column order. Auto-generated from columns if omitted. */
  defaultColumnOrder?: C['id'][];

  /** Default column visibility map. */
  defaultColumnVisibility?: IDColumnVisibility<C['id']>;

  /**
   * Externally-controlled default column visibility setter.
   *
   * When provided, the hook treats `defaultColumnVisibility` as controlled
   * state and delegates updates to this setter instead of managing its own
   * internal state.
   */
  setDefaultColumnVisibility?: (visibility: IDColumnVisibility<C['id']>) => void;

  /** Default pagination offset. */
  defaultOffset?: number;

  /** Default pagination page size. */
  defaultLimit?: number;

  /** Whether the data represents a page or the complete data. */
  isServerSidePaginated?: boolean;

  /** Default sorting state. */
  defaultSortingState?: IDColumnSort<C['id']>[];

  /** Default column filters. */
  defaultColumnFilters?: IDColumnFilter<C['id']>[];

  /** Default global filter string. */
  defaultGlobalFilter?: string;

  /** Enable row drag-and-drop reordering. */
  enableRowReordering?: boolean;

  /** Default row order. */
  defaultRowOrder?: D[];

  /** Externally-controlled editing state. */
  isEditing?: boolean;

  /** Externally-controlled editing state setter. */
  setIsEditing?: React.Dispatch<React.SetStateAction<boolean>>;

  /**
   * Optional state overrides — callers provide their own [value, setter] pairs
   * (e.g. for persistence) instead of the hook's internal useState.
   */
  overrides?: TableStateOverrides<C['id'], D>;
}

export interface TableStateProps<
  D = any,
  C extends SharedColumnDef<D, any> = SharedColumnDef<D, any>,
> {
  config: TableStateConfig<D, C>;
  children: (state: AutoCollectionStateInterface<D, C['id']>) => ReactNode;
}

/**
 * Computes the effective default column order from column definitions.
 *
 * If `defaultColumnOrder` is provided and non-empty it is returned as-is.
 * Otherwise columns are sorted so that `fixedPosition: 'start'` columns come
 * first and `fixedPosition: 'end'` columns come last.
 */
export const useEffectiveDefaultColumnOrder = <
  D extends any = any,
  C extends SharedColumnDef<D, any> = SharedColumnDef<D, any>,
>(
  columns: C[],
  defaultColumnOrder?: C['id'][],
): C['id'][] =>
  useMemo((): C['id'][] => {
    if (defaultColumnOrder && defaultColumnOrder.length > 0) {
      return defaultColumnOrder;
    }

    const sortedColumns = [...columns].sort((a, b) => {
      const aFixed = a.meta?.fixedPosition;
      const bFixed = b.meta?.fixedPosition;

      if (aFixed && bFixed) {
        if (aFixed === 'start' && bFixed === 'end') return -1;
        if (aFixed === 'end' && bFixed === 'start') return 1;
        return 0;
      }

      if (aFixed && !bFixed) {
        return aFixed === 'start' ? -1 : 1;
      }
      if (!aFixed && bFixed) {
        return bFixed === 'start' ? 1 : -1;
      }

      return 0;
    });

    return sortedColumns.map((col) => col.id!).filter(Boolean);
  }, [columns, defaultColumnOrder]);

/**
 * Computes the effective default column visibility, ensuring columns with
 * `meta.alwaysVisible` are forced visible
 */
export const useEffectiveDefaultColumnVisibility = <
  D extends any = any,
  C extends SharedColumnDef<D, any> = SharedColumnDef<D, any>,
>(
  columns: C[],
  defaultColumnVisibility: IDColumnVisibility<C['id']>,
): IDColumnVisibility<C['id']> =>
  useMemo((): IDColumnVisibility<C['id']> => {
    const visibility: IDColumnVisibility<C['id']> = { ...defaultColumnVisibility };

    columns.forEach((col) => {
      if (col.meta?.alwaysVisible && col.id) {
        visibility[col.id as C['id']] = true;
      }
    });

    return visibility;
  }, [columns, defaultColumnVisibility]);

/**
 * What a column is set to when `visibility` has no entry for it, e.g. a saved state that predates
 * the column: the table's default rather than tanstack's implicit `true`.
 */
export function withDefaultColumnVisibility(
  visibility: IDColumnVisibility<string>,
  defaults: IDColumnVisibility<string>,
): IDColumnVisibility<string> {
  return { ...defaults, ...visibility };
}

/**
 * Extends `useState` with an optional external override.
 *
 * Internal state always runs (stable hook order), but the override tuple
 * takes precedence when provided.
 */
const useOverridableState = <T,>(defaultValue: T, override?: StateTuple<T>): StateTuple<T> => {
  const internal = useState(defaultValue);
  return override ?? internal;
};

/** Holds a value's identity steady while its contents are unchanged; most callers pass literals. */
export function useStableIdentity<T>(value: T): T {
  const contents = JSON.stringify(value);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on contents, not identity
  return useMemo(() => value, [contents]);
}

/**
 * Core table state management hook.
 *
 * Manages column order/visibility, sorting, pagination, filters, global filter,
 * row reordering, and editing state. Callers can provide `overrides` to replace
 * internal state with externally-managed [value, setter] pairs.
 */
export function useTableState<
  D extends any = unknown,
  C extends SharedColumnDef<D, any> = SharedColumnDef<D, any>,
>({
  columns,
  defaultColumnOrder: defaultColumnOrderProp,
  defaultColumnVisibility: defaultColumnVisibilityProp = {},
  setDefaultColumnVisibility: setDefaultColumnVisibilityProp,
  defaultOffset = 0,
  defaultLimit = 50,
  isServerSidePaginated = false,
  defaultSortingState: defaultSortingStateProp = [],
  defaultColumnFilters: defaultColumnFiltersProp = [],
  defaultGlobalFilter = '',
  enableRowReordering = false,
  defaultRowOrder: defaultRowOrderProp = [],
  isEditing,
  setIsEditing,
  overrides,
}: TableStateConfig<D, C>): AutoCollectionStateInterface<D, C['id']> {
  const defaultColumnOrder = useStableIdentity(defaultColumnOrderProp);
  const defaultSortingState = useStableIdentity(defaultSortingStateProp);
  const defaultColumnFilters = useStableIdentity(defaultColumnFiltersProp);
  const stableDefaultColumnVisibility = useStableIdentity(defaultColumnVisibilityProp);

  const effectiveDefaultColumnOrder = useEffectiveDefaultColumnOrder(columns, defaultColumnOrder);

  const [defaultColumnVisibility, setDefaultColumnVisibility] = useOverridableState(
    stableDefaultColumnVisibility,
    setDefaultColumnVisibilityProp
      ? [stableDefaultColumnVisibility, setDefaultColumnVisibilityProp]
      : undefined,
  );

  const effectiveDefaultColumnVisibility = useEffectiveDefaultColumnVisibility(
    columns,
    defaultColumnVisibility,
  );

  const [defaultRowOrder, setDefaultRowOrder] = useState(defaultRowOrderProp);

  // State — overrides take precedence over internal state

  const [columnOrder, setColumnOrderRaw] = useOverridableState(
    effectiveDefaultColumnOrder,
    overrides?.columnOrder,
  );

  const [storedColumnVisibility, setColumnVisibilityRaw] = useOverridableState(
    effectiveDefaultColumnVisibility as VisibilityState,
    overrides?.columnVisibility,
  );

  const columnVisibility = useMemo(
    () => withDefaultColumnVisibility(storedColumnVisibility, effectiveDefaultColumnVisibility),
    [storedColumnVisibility, effectiveDefaultColumnVisibility],
  );

  const [columnFilters, setColumnFilters] = useOverridableState(
    defaultColumnFilters as ColumnFiltersState,
    overrides?.columnFilters,
  );

  const [sortingState, setSortingState] = useState(defaultSortingState);
  const [globalFilter, setGlobalFilter] = useOverridableState(
    defaultGlobalFilter,
    overrides?.globalFilter,
  );
  const [offset, setOffset] = useState(defaultOffset);
  const [limit, setLimit] = useState(defaultLimit);
  const [numRows, setNumRowsRaw] = useState(0);

  const [rowOrder, setRowOrder] = useOverridableState(defaultRowOrderProp, overrides?.rowOrder);

  const setNumRows = useCallback(
    (newNumRows: number) => {
      setNumRowsRaw((prev) => {
        const nextOffset = offset !== 0 && offset >= newNumRows ? 0 : offset;
        if (nextOffset !== offset) {
          setOffset(nextOffset);
        }
        return newNumRows === prev ? prev : newNumRows;
      });
    },
    [offset],
  );

  // Debounced filters

  const [debouncedColumnFilters, { isPending: isColumnFiltersPending }] = useDebounce(
    columnFilters,
    150,
    { equalityFn: (a, b) => JSON.stringify(a) === JSON.stringify(b) },
  );

  const [debouncedGlobalFilter, { isPending: isGlobalFilterPending }] = useDebounce(
    globalFilter,
    150,
  );

  const isFilterPending = isColumnFiltersPending() || isGlobalFilterPending();

  const tableRef = useRef<AutoCollectionHandle<D> | null>(null);

  const [internalIsEditing, setInternalIsEditing] = useState(false);

  // Setters (with constraint enforcement)

  const setColumnOrder = useCallback(
    (newOrder: C['id'][]) => {
      const startFixedColumns = columns
        .filter((col) => col.meta?.fixedPosition === 'start')
        .map((col) => col.id!)
        .filter(Boolean);

      const endFixedColumns = columns
        .filter((col) => col.meta?.fixedPosition === 'end')
        .map((col) => col.id!)
        .filter(Boolean);

      const nonFixedOrder = newOrder.filter(
        (id) => !startFixedColumns.includes(id) && !endFixedColumns.includes(id),
      );

      const finalOrder = [...startFixedColumns, ...nonFixedOrder, ...endFixedColumns] as C['id'][];
      setColumnOrderRaw(finalOrder);
    },
    [columns, setColumnOrderRaw],
  );

  const setColumnVisibility = useCallback(
    (newVisibility: IDColumnVisibility<C['id']>) => {
      const validatedVisibility: IDColumnVisibility<C['id']> = { ...newVisibility };

      columns.forEach((col) => {
        if (col.meta?.alwaysVisible && col.id) {
          validatedVisibility[col.id as C['id']] = true;
        }
      });

      setColumnVisibilityRaw(validatedVisibility as VisibilityState);
    },
    [columns, setColumnVisibilityRaw],
  );

  // Reordering

  const handleColumnReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;

      const currentOrder = columnOrder;
      const next = [...currentOrder];

      const movedColumnId = currentOrder[fromIndex];
      const movedColumnDef = columns.find((col) => col.id === movedColumnId);

      if (movedColumnDef?.meta?.fixedPosition) {
        return;
      }

      const [movedColumn] = next.splice(fromIndex, 1);
      if (movedColumn) {
        next.splice(toIndex, 0, movedColumn);
      }
      setColumnOrder(next);
    },
    [columnOrder, setColumnOrder, columns],
  );

  const handleRowReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (!enableRowReordering) return;
      if (fromIndex === toIndex) return;

      const next = [...rowOrder];
      const [movedItem] = next.splice(fromIndex + offset, 1);
      if (movedItem) {
        next.splice(toIndex + offset, 0, movedItem);
      }
      setRowOrder(next);
    },
    [enableRowReordering, rowOrder, setRowOrder, offset],
  );

  // Resets

  const resetColumnOrder = useCallback(() => {
    setColumnOrder(effectiveDefaultColumnOrder);
  }, [effectiveDefaultColumnOrder, setColumnOrder]);

  const resetColumnVisibility = useCallback(() => {
    setColumnVisibility(effectiveDefaultColumnVisibility);
  }, [effectiveDefaultColumnVisibility, setColumnVisibility]);

  const resetSorting = useCallback(() => {
    setSortingState(defaultSortingState);
  }, [defaultSortingState]);

  const resetToDefaultSorting = useCallback(() => {
    setSortingState(defaultSortingState);
  }, [defaultSortingState]);

  const resetFilters = useCallback(() => {
    setColumnFilters(defaultColumnFilters as ColumnFiltersState);
  }, [defaultColumnFilters, setColumnFilters]);

  const resetGlobalFilter = useCallback(() => {
    setGlobalFilter(defaultGlobalFilter);
  }, [defaultGlobalFilter, setGlobalFilter]);

  const resetPagination = useCallback(() => {
    setOffset(defaultOffset);
    setLimit(defaultLimit);
    setNumRowsRaw(0);
  }, [defaultOffset, defaultLimit]);

  const resetToFirstPage = useCallback(() => {
    setOffset(0);
  }, []);

  const resetRowOrder = useCallback(() => {
    setRowOrder(defaultRowOrder);
  }, [defaultRowOrder, setRowOrder]);

  const resetAll = useCallback(() => {
    resetColumnOrder();
    resetColumnVisibility();
    resetSorting();
    resetFilters();
    resetGlobalFilter();
    resetPagination();
    if (enableRowReordering) {
      resetRowOrder();
    }
  }, [
    resetColumnOrder,
    resetColumnVisibility,
    resetSorting,
    resetFilters,
    resetGlobalFilter,
    resetPagination,
    enableRowReordering,
    resetRowOrder,
  ]);

  // Derived

  const isRowOrderDirty = useMemo(() => {
    if (!enableRowReordering) return false;
    if (defaultRowOrder.length !== rowOrder.length) return true;
    return defaultRowOrder.some((item, index) => item !== rowOrder[index]);
  }, [enableRowReordering, defaultRowOrder, rowOrder]);

  const isColumnOrderDefault = useMemo(() => {
    if (effectiveDefaultColumnOrder.length !== columnOrder.length) return false;
    return effectiveDefaultColumnOrder.every((id, index) => id === columnOrder[index]);
  }, [columnOrder, effectiveDefaultColumnOrder]);

  const isColumnVisibilityDefault = useMemo(() => {
    const defaultKeys = Object.keys(effectiveDefaultColumnVisibility).filter(
      (key) => effectiveDefaultColumnVisibility[key as C['id']] === false,
    );
    const currentKeys = Object.keys(columnVisibility).filter(
      (key) => columnVisibility[key] === false,
    );
    if (defaultKeys.length !== currentKeys.length) return false;
    return defaultKeys.every(
      (key) => effectiveDefaultColumnVisibility[key as C['id']] === columnVisibility[key],
    );
  }, [columnVisibility, effectiveDefaultColumnVisibility]);

  const isSortingDefault = useMemo(() => {
    if (defaultSortingState.length !== sortingState.length) return false;
    return defaultSortingState.every(
      (sort, index) =>
        sort.id === sortingState[index]?.id && sort.desc === sortingState[index]?.desc,
    );
  }, [sortingState, defaultSortingState]);

  const isFiltersDefault = useMemo(() => {
    if (defaultColumnFilters.length !== columnFilters.length) return false;
    return defaultColumnFilters.every(
      (filter, index) =>
        filter.id === columnFilters[index]?.id && filter.value === columnFilters[index]?.value,
    );
  }, [columnFilters, defaultColumnFilters]);

  const isGlobalFilterDefault = useMemo(
    () => defaultGlobalFilter === globalFilter,
    [globalFilter, defaultGlobalFilter],
  );

  const isPaginationDefault = useMemo(
    () => offset === defaultOffset && limit === defaultLimit && numRows === 0,
    [offset, defaultOffset, limit, defaultLimit, numRows],
  );

  // A new state object re-renders every row, so hold it steady across a parent's renders.
  return useMemo(
    () => ({
      tableRef,

      // Column management
      columnOrder,
      setColumnOrder,
      defaultColumnOrder: effectiveDefaultColumnOrder,
      resetColumnOrder,
      handleColumnReorder,

      columnVisibility: columnVisibility as IDColumnVisibility<C['id']>,
      setColumnVisibility,
      defaultColumnVisibility: effectiveDefaultColumnVisibility,
      setDefaultColumnVisibility,
      resetColumnVisibility,

      // Row management
      enableRowReordering,
      rowOrder,
      setRowOrder,
      defaultRowOrder,
      setDefaultRowOrder,
      isRowOrderDirty,
      resetRowOrder,
      handleRowReorder,

      // Pagination
      offset,
      setOffset,
      limit,
      setLimit,
      numRows,
      setNumRows,
      resetPagination,
      resetToFirstPage,
      isServerSidePaginated,

      // Sorting
      sortingState,
      setSortingState,
      defaultSortingState,
      resetSorting,
      resetToDefaultSorting,

      // Filters
      columnFilters: columnFilters as ColumnFiltersState,
      setColumnFilters,
      defaultColumnFilters,
      resetFilters,
      debouncedColumnFilters: debouncedColumnFilters as ColumnFiltersState,
      debouncedGlobalFilter,
      isFilterPending,

      // Global filter
      globalFilter,
      setGlobalFilter,
      defaultGlobalFilter,
      resetGlobalFilter,

      // Editing
      isEditing: isEditing ?? internalIsEditing,
      setIsEditing: setIsEditing ?? setInternalIsEditing,

      // Utilities
      resetAll,
      isColumnOrderDefault,
      isColumnVisibilityDefault,
      isSortingDefault,
      isFiltersDefault,
      isPaginationDefault,
      isGlobalFilterDefault,
      isCurrentTimePeriodDefault: true,
    }),
    [
      columnOrder,
      setColumnOrder,
      effectiveDefaultColumnOrder,
      resetColumnOrder,
      handleColumnReorder,
      columnVisibility,
      setColumnVisibility,
      effectiveDefaultColumnVisibility,
      setDefaultColumnVisibility,
      resetColumnVisibility,
      enableRowReordering,
      rowOrder,
      setRowOrder,
      defaultRowOrder,
      isRowOrderDirty,
      resetRowOrder,
      handleRowReorder,
      offset,
      limit,
      numRows,
      setNumRows,
      resetPagination,
      resetToFirstPage,
      isServerSidePaginated,
      sortingState,
      defaultSortingState,
      resetSorting,
      resetToDefaultSorting,
      columnFilters,
      setColumnFilters,
      defaultColumnFilters,
      resetFilters,
      debouncedColumnFilters,
      debouncedGlobalFilter,
      isFilterPending,
      globalFilter,
      setGlobalFilter,
      defaultGlobalFilter,
      resetGlobalFilter,
      isEditing,
      internalIsEditing,
      setIsEditing,
      resetAll,
      isColumnOrderDefault,
      isColumnVisibilityDefault,
      isSortingDefault,
      isFiltersDefault,
      isPaginationDefault,
      isGlobalFilterDefault,
    ],
  );
}

/**
 * Component wrapper for useTableState.
 *
 * Renders children as a function receiving the table state.
 */
export function TableState<D = any>({ config, children: Children }: TableStateProps<D>) {
  const state = useTableState(config);
  return <Children {...state} />;
}

export function useTanstackTableState<D extends any>(
  tableState: AutoCollectionStateInterface<D, any>,
) {
  return useMemo(() => {
    const s: Partial<ReactTableState> = {
      globalFilter: tableState.globalFilter,
    };

    if (tableState.sortingState) {
      s.sorting = tableState.sortingState;
    }

    if (tableState.columnVisibility) {
      s.columnVisibility = tableState.columnVisibility as VisibilityState;
    }

    if (tableState.columnFilters) {
      s.columnFilters = tableState.columnFilters;
    }

    if (tableState.columnOrder) {
      s.columnOrder = tableState.columnOrder;
    }

    return s;
  }, [tableState]);
}

export function useHeadlessTable<D extends any, C extends ColumnDef<D, any>>({
  tableState,
  columns,
  data,
  onColumnFiltersChange,
}: {
  tableState: AutoCollectionStateInterface<D, NonNullable<C['id']>>;
  data: D[];
  columns: C[];
  onColumnFiltersChange?: (rows: D[]) => void;
}) {
  const { columnFilters } = tableState;
  const state = useTanstackTableState(tableState);

  const table = createTable<D>({
    data,
    columns,
    enableColumnFilters: true,
    sortingFns,
    filterFns,
    state,
    onStateChange: () => {},
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    renderFallbackValue: null,
  });

  const setRows = useEffectEvent(() => {
    const rows = table.getRowModel().rows.map((r) => r.original);
    onColumnFiltersChange?.(rows);
  });

  useEffect(() => {
    if (!onColumnFiltersChange) return;
    if (!columnFilters.length) {
      onColumnFiltersChange(data);
    }
    setRows();
  }, [onColumnFiltersChange, data, columnFilters]);

  return table;
}
